import {Typography} from '@mui/material';
import {DlMuiDynamicIcon} from '@wallaby-ui/core';
import {isValid, parseISO} from 'date-fns';
import {isEmpty, isEqual} from 'lodash';
import * as React from 'react';
import {useCallback, useRef, useState} from 'react';

export type ValidateErrorStrings = Record<string, string>;
type GetValidateErrorStrings = () => ValidateErrorStrings;
export type ValidateError = {
  bool: boolean;
  string: string;
};
type ValidateErrors = Record<string, ValidateError>;
type Validator = {
  errors: ValidateErrors;
  haveErrors: boolean;
  validate: () => boolean;
};
export function isValidDate(value: any): value is Date {
  return isValid(value);
}
export function isValidDateString(value: any) {
  return isValid(parseISO(value));
}
export function useValidate(getErrorsStrings: GetValidateErrorStrings): Validator {
  const [didValidateOnce, setDidValidateOnce] = useState(false);
  const errors = useRef({} as ValidateErrors);
  const recomputeErrors = useCallback(() => {
    const errorStrings = getErrorsStrings();
    const errorStringEntries = Object.entries(errorStrings);
    const newErrors: ValidateErrors = Object.fromEntries(
      errorStringEntries
        .map(
          ([k, v]) =>
            [
              k,
              {
                bool: Boolean(v),
                string: v,
              },
            ] as [string, ValidateError]
        )
        .filter(([_, v]) => v.bool)
    );
    return newErrors;
  }, [getErrorsStrings]);
  if (didValidateOnce) {
    errors.current = recomputeErrors();
  }
  const validate = useCallback(() => {
    setDidValidateOnce(true);
    errors.current = recomputeErrors();
    return isEqual(errors.current, {});
  }, [recomputeErrors]);
  return {
    errors: errors.current,
    haveErrors: !isEqual(errors.current, {}),
    validate,
  };
}

export function getMultiFilter(values: any[]): boolean[] {
  const isEmptyArr = values.map(isEmpty);
  return Array(values.length)
    .fill(0)
    .map((_, i) => {
      const hasValueBefore = !isEmptyArr?.[i - 1];
      const hasValueAtOrAfter = isEmptyArr.slice(i).reduce((acc, v) => acc || !v, false);
      return hasValueBefore || hasValueAtOrAfter;
    });
}
export type BoolFilterType = ('TRUE' | 'FALSE')[] | undefined | null;
export const BOOL_FILTER_OPTIONS = [
  // {id: 'ALL', messageKey: 'global.boolFilter.all'},
  {id: 'TRUE', messageKey: 'global.boolFilter.true'},
  {id: 'FALSE', messageKey: 'global.boolFilter.false'},
];
/** map multiselect `('TRUE' | 'FALSE')[]` to `boolean | undefined` */
export function getBoolFilterValue(boolFilterOrNullsy: BoolFilterType): boolean | undefined {
  const boolFilter = boolFilterOrNullsy ?? [];
  if (boolFilter.length === 1) {
    return boolFilter[0] === 'TRUE';
  }
  return undefined;
}
export function getSingleBoolFilterValue(value?: 'TRUE' | 'FALSE'): boolean | undefined {
  if (value == null) return undefined;
  return value === 'TRUE';
}
export function getSingleBoolFilterOptionId(value?: boolean): string | undefined {
  if (value == null) return undefined;
  return value ? 'TRUE' : 'FALSE';
}

type LightErrorBoxProps = {
  message: string;
};
export const LightErrorBox: React.FC<LightErrorBoxProps> = (props) => {
  const {message} = props;
  return (
    <div
      style={{
        borderRadius: 8,
        width: '100%',
        padding: '4px 8px',
        background: '#ffd7d9',
        color: '#da1e28',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
      }}
      data-name='invalid-changes'
    >
      <DlMuiDynamicIcon
        iconName='haidiWarningIcon'
        useFixedViewBox='0 0 13 12'
        style={{
          color: 'inherit',
          padding: 0,
          margin: 0,
          pointerEvents: 'none',
        }}
        svgIconStyle={{fontSize: 16}}
        noButtonStyles
      />
      <Typography style={{wordBreak: 'break-word'}} variant='value'>
        {message}
      </Typography>
    </div>
  );
};
